/**
 * OffAir — the sign-off section at the bottom of the home page.
 *
 * Not a separate picture: it's the SAME background video, continuing under
 * the section, but scrambled the way a premium cable channel looked without
 * the box — alternating bands with inverted luminance, horizontal sync
 * slipping band by band, hue-swapped stripes, a slow vertical roll. You can
 * almost make out what's on. The scramble ramps with scroll progress and is
 * capped so it never turns to mush; the live glitchGL background crossfades
 * out as this canvas (showing the same frames) fades in, so the hand-off is
 * continuous rather than a hard cut.
 *
 * Progress p: 0 when the section's top reaches the bottom of the viewport,
 * 1 when the section fills it (it's the last thing on the page).
 */

const VS = `
attribute vec2 aPos;
varying vec2 vUv;
void main() { vUv = aPos; gl_Position = vec4(aPos * 2.0 - 1.0, 0.0, 1.0); }
`;

const FS = `
precision mediump float;
uniform sampler2D uTex;
uniform float uHasTex;
uniform vec2  uRes;
uniform vec2  uTexAspect;  // cover-fit scale
uniform float uTime;
uniform float uP;
uniform float uTune;     // 0 = fully scrambled, 1 = locked clean
uniform vec3  uAccent;
varying vec2  vUv;

float h(vec2 p) { return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }

vec3 src(vec2 uv) {
  if (uHasTex < 0.5) {
    // no video yet: a quiet dark field with a faint grid so the scramble still reads
    float g = step(0.97, fract(uv.x * 24.0)) + step(0.97, fract(uv.y * 14.0));
    return vec3(0.05) + g * 0.05;
  }
  vec2 s = (uv - 0.5) * uTexAspect + 0.5;
  s = clamp(s, 0.0, 1.0);
  return texture2D(uTex, vec2(s.x, 1.0 - s.y)).rgb;
}

void main() {
  float p = clamp(uP, 0.0, 1.0);
  // scramble strength: nothing at the seam, full lock-out once settled. Capped.
  // The tuner knob pulls it back toward clean; a little residual flutter stays
  // even when locked so it still feels like a signal, not a file.
  float k = smoothstep(0.18, 0.82, p) * (1.0 - uTune * 0.96);
  k += 0.025 * uTune * (0.5 + 0.5 * sin(uTime * 9.0 + vUv.y * 30.0));

  vec2 uv = vUv;
  // slow vertical roll as the hold fails (max ~10% of height)
  uv.y = fract(uv.y + k * 0.10 * fract(uTime * 0.09));

  // Videocipher-style bands: ~22 bands, each with its own sync slip + polarity
  float bands = 22.0;
  float b = floor(uv.y * bands + uTime * 0.35 * k);
  float rb = h(vec2(b, 7.0));
  float rb2 = h(vec2(b, floor(uTime * 2.0)));
  float slip = (rb - 0.5) * 0.5 * k;               // up to ±25% width
  slip += (rb2 - 0.5) * 0.06 * k;                   // jitter
  uv.x = fract(uv.x + slip);

  // Random macroblock pixelation: a coarse grid of cells, each with its own
  // size and a flicker so blocks pop in and out — digital breakup on top of
  // the analog band scramble. Scales with k; some cells stay sharp.
  float cellN = mix(18.0, 10.0, k);                          // cells across
  vec2 cellId = floor(vec2(uv.x * cellN, uv.y * cellN * 0.56));
  float cr = h(cellId + floor(uTime * 3.0));
  float cr2 = h(cellId * 1.7 + vec2(5.0, 2.0) + floor(uTime * 1.5));
  float blocky = step(0.45, cr) * k;                         // which cells are mosaicked
  float sub = mix(2.0, 8.0, cr2);                            // block size within the cell
  vec2 quant = (floor(uv * cellN * vec2(1.0, 0.56) * sub) + 0.5) / (cellN * vec2(1.0, 0.56) * sub);
  vec2 suv = mix(uv, quant, blocky);
  // a few cells freeze on a stale offset (smeared macroblocks)
  float stale = step(0.85, cr2) * k;
  suv.x = mix(suv.x, fract(suv.x + (cr - 0.5) * 0.2), stale);

  vec3 c = src(suv);
  float luma = dot(c, vec3(0.299, 0.587, 0.114));
  // blocked cells get a slight brightness step so the mosaic reads
  c += (cr - 0.5) * 0.12 * blocky;

  // inverted-polarity bands (the classic look): roughly a third of the bands flip
  float inv = step(0.62, h(vec2(b, 3.0))) * k;
  c = mix(c, vec3(1.0) - c, inv * 0.7);

  // colour cast per band — the green / magenta / amber of a scrambled feed,
  // applied as a tint so it shows even on a monochrome source
  float hsel = h(vec2(b, 11.0));
  vec3 tint = vec3(1.0, 0.85, 0.5);
  if (hsel < 0.33) tint = vec3(0.55, 1.0, 0.6);
  else if (hsel < 0.66) tint = vec3(1.0, 0.55, 0.95);
  c = mix(c, c * tint + 0.06 * (tint - 0.5), 0.75 * k);
  // and the occasional channel swap on top
  c = mix(c, c.gbr, step(0.8, h(vec2(b, 17.0))) * 0.7 * k);

  // a little contrast so shapes stay readable under the bands
  c = mix(vec3(luma), c, 1.0 + 0.25 * k);

  // fine horizontal hash + scanlines
  float n = h(floor(vUv * uRes / 2.0) + floor(uTime * 24.0));
  c += (n - 0.5) * 0.10 * k;
  c *= 1.0 - 0.10 * (0.5 + 0.5 * sin(gl_FragCoord.y * 3.14159));

  // a faint accent wash so it sits with the current theme
  c = mix(c, c * (0.7 + 0.3 * uAccent), 0.35 * k);

  // darken slightly as it settles so the type on top reads (less when tuned in)
  c *= 1.0 - 0.22 * smoothstep(0.6, 1.0, p) * (1.0 - 0.6 * uTune);

  gl_FragColor = vec4(c, 1.0);
}
`;

function hex3(cssColor, fallback = [0.94, 0.92, 0.88]) {
  const m = /^#?([0-9a-f]{6})$/i.exec((cssColor || '').trim());
  if (!m) return fallback;
  const n = parseInt(m[1], 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

export class OffAir {
  /**
   * @param {HTMLElement} section — #offair
   * @param {Object} [o]
   * @param {HTMLElement} [o.background] — #background wrapper (glitchGL) to crossfade out
   * @param {HTMLVideoElement} [o.video] — the live background <video> to scramble
   * @param {{ isEnabled: boolean }} [o.audio] — AudioManager; scramble buzz only if enabled
   */
  constructor(section, { background = null, video = null, audio = null } = {}) {
    this.section = section;
    this.stage = section.querySelector('.offair__stage') || section;
    this.ring = section.querySelector('.offair__ring');
    this.tuner = section.querySelector('.tuner');
    this.canvas = section.querySelector('.offair__canvas');
    this.copy = section.querySelector('.offair__copy');
    this.pre = section.querySelector('.offair__pre');
    this.links = section.querySelector('.offair__links');
    this.fine = section.querySelector('.offair__fine');
    this.status = section.querySelector('.offair__status');
    this.background = background;
    this.video = video;
    this.audio = audio;
    this.p = 0;
    this._visible = false;
    this._raf = null;
    this._t0 = performance.now();
    this._lastW = 0;
    this._lastH = 0;
    this._tone = null;

    const gl = this.canvas.getContext('webgl', { alpha: false, antialias: false, preserveDrawingBuffer: true });
    if (!gl) { this.canvas.style.display = 'none'; return; }
    this.gl = gl;
    const compile = (type, src) => {
      const s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) console.error('[OffAir] shader:', gl.getShaderInfoLog(s));
      return s;
    };
    const prog = gl.createProgram();
    gl.attachShader(prog, compile(gl.VERTEX_SHADER, VS));
    gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, FS));
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) console.error('[OffAir] link:', gl.getProgramInfoLog(prog));
    gl.useProgram(prog);
    const vbo = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, 'aPos');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    const U = (n) => gl.getUniformLocation(prog, n);
    this.u = { res: U('uRes'), time: U('uTime'), p: U('uP'), tune: U('uTune'), accent: U('uAccent'), hasTex: U('uHasTex'), aspect: U('uTexAspect') };

    this.tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, this.tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([12, 12, 14, 255]));
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

    if (typeof IntersectionObserver !== 'undefined') {
      this._io = new IntersectionObserver((es) => es.forEach((e) => this._setVisible(e.isIntersecting)), { threshold: 0.01 });
      this._io.observe(section);
    } else {
      this._setVisible(true);
    }
    this._onResize = () => this._measure();
    window.addEventListener('resize', this._onResize, { passive: true });
    this._measure();
    this._setupTuner();
  }

  // ── the tuner: a knob with a hidden sweet spot, a meter, and drift ──────

  _setupTuner() {
    this.tuner = this.section.querySelector('.tuner');
    this.knob = this.section.querySelector('.tuner__knob');
    this.meterBars = [...this.section.querySelectorAll('.tuner__meter i')];
    this.tune = { value: 0.5, target: 0.22 + Math.random() * 0.56, clarity: 0, locked: false, lockedAt: 0, drift: 0 };
    if (!this.knob) return;

    const setValue = (v) => {
      this.tune.value = Math.max(0, Math.min(1, v));
      this.knob.style.setProperty('--angle', `${-135 + this.tune.value * 270}deg`);
      this.knob.setAttribute('aria-valuenow', String(Math.round(this.tune.value * 100)));
    };
    setValue(this.tune.value);

    // drag: horizontal + vertical movement both turn the knob (like a real dial)
    let dragging = false, lastX = 0, lastY = 0;
    this.knob.addEventListener('pointerdown', (e) => {
      dragging = true; lastX = e.clientX; lastY = e.clientY;
      this.knob.setPointerCapture(e.pointerId);
      this.knob.classList.add('is-dragging');
      e.preventDefault();
    });
    this.knob.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      const dx = e.clientX - lastX, dy = e.clientY - lastY;
      lastX = e.clientX; lastY = e.clientY;
      setValue(this.tune.value + (dx - dy) / 420);   // ~420px of travel end to end
    });
    const stop = () => { dragging = false; this.knob.classList.remove('is-dragging'); };
    this.knob.addEventListener('pointerup', stop);
    this.knob.addEventListener('pointercancel', stop);
    this.knob.addEventListener('wheel', (e) => { e.preventDefault(); setValue(this.tune.value + (e.deltaY + e.deltaX) / 1800); }, { passive: false });
    this.knob.addEventListener('keydown', (e) => {
      const step = e.shiftKey ? 0.05 : 0.01;
      if (e.key === 'ArrowRight' || e.key === 'ArrowUp') { setValue(this.tune.value + step); e.preventDefault(); }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') { setValue(this.tune.value - step); e.preventDefault(); }
    });
    this._setValue = setValue;

    // Channel change (dice, or clicking the canvas cycles the preset) → the
    // station moves: new hidden sweet spot, you tune again from where you are.
    const retune = () => {
      this.tune.target = 0.1 + Math.random() * 0.8;
      this.tune.locked = false;
      this.tune.drift = 0;
      this.tuner?.classList.add('is-retuning');
      setTimeout(() => this.tuner?.classList.remove('is-retuning'), 500);
    };
    document.addEventListener('randomize', retune);
    this._themeObs = new MutationObserver(retune);
    this._themeObs.observe(document.body, { attributes: true, attributeFilter: ['data-theme'] });
    this._retune = retune;

    // A thump on the set: clicking the picture knocks the signal for a beat
    this.canvas.addEventListener('click', () => {
      this.tune.clarity = Math.max(0, this.tune.clarity - 0.6);
      this.stage.classList.add('is-thumped');
      setTimeout(() => this.stage.classList.remove('is-thumped'), 220);
    });
  }

  _updateTuner(now) {
    const t = this.tune; if (!t) return;
    // Clarity falls off around the sweet spot; ±0.015 is clean, ±0.08 is the shoulder.
    const d = Math.abs(t.value - t.target);
    const raw = Math.exp(-(d * d) / (2 * 0.035 * 0.035));
    t.clarity += (raw - t.clarity) * 0.18;                 // smooth so it feels analog
    const locked = t.clarity > 0.9;
    if (locked && !t.locked) { t.locked = true; t.lockedAt = now; }
    if (!locked) t.locked = false;
    // Hold a lock for a few seconds and the station drifts — chase it.
    if (t.locked && now - t.lockedAt > 4500) {
      t.drift += (Math.random() - 0.5) * 0.0009;
      t.drift = Math.max(-0.0025, Math.min(0.0025, t.drift));
      t.target = Math.max(0.08, Math.min(0.92, t.target + t.drift));
    }
    if (this.meterBars.length) {
      const lit = Math.round(t.clarity * this.meterBars.length);
      this.meterBars.forEach((b, i) => b.classList.toggle('is-on', i < lit));
    }
    this.tuner?.classList.toggle('is-locked', locked);
    return t.clarity;
  }

  _setVisible(on) {
    this._visible = on;
    if (on && !this._raf) this._raf = requestAnimationFrame(this._tick);
    if (!on) { this._stopTone(); if (this.background) this.background.style.opacity = ''; this.stage.style.opacity = '0'; this.stage.classList.remove('is-live', 'is-interactive'); }
  }

  /** Measured every frame while visible — no dependence on scroll event plumbing. */
  _measure() {
    const r = this.section.getBoundingClientRect();
    const vh = window.innerHeight;
    // Runway progress: 0 when the runway's top reaches the bottom of the
    // viewport, 1 when its bottom does (end of page). The stage is fixed, so
    // nothing slides — the picture just takes over in place.
    const p = Math.max(0, Math.min(1, (vh - r.top) / Math.max(1, r.height)));
    if (Math.abs(p - this.p) < 0.0005) return;
    this.p = p;
    this.section.style.setProperty('--offair-p', p.toFixed(3));

    // Stage fades in over the page as the signal fails
    const alpha = Math.max(0, Math.min(1, p / 0.42));
    this.stage.style.opacity = String(alpha);
    this.stage.classList.toggle('is-live', p > 0.001);
    this.stage.classList.toggle('is-interactive', p > 0.8);

    // The live glitchGL background hands off once we're mostly opaque
    if (this.background) {
      const fade = Math.max(0, Math.min(1, (p - 0.3) / 0.4));
      this.background.style.opacity = String(1 - fade);
    }

    // Ring: spins with scroll, then shrinks away as the copy lands
    if (this.ring) {
      const out = Math.max(0, Math.min(1, (p - 0.55) / 0.25));
      this.ring.style.setProperty('--ring-rot', `${(p * 300).toFixed(1)}deg`);
      this.ring.style.setProperty('--ring-scale', (1 - out * 0.65).toFixed(3));
      this.ring.style.setProperty('--ring-alpha', (1 - out).toFixed(3));
    }
    if (this.tuner) {
      const tr = Math.max(0, Math.min(1, (p - 0.6) / 0.25));
      this.tuner.style.opacity = String(tr);
      this.tuner.style.transform = `translateY(${(1 - tr) * 12}px)`;
    }
    // Supporting lines fade in late. The title is driven by OffAirTitle so
    // its letters can assemble (and fly apart again) without being faded out.
    if (this.copy) {
      const reveal = Math.max(0, Math.min(1, (p - 0.72) / 0.22));
      this.copy.style.transform = `translateY(${(1 - reveal) * 14}px)`;
      const fade = String(reveal);
      if (this.pre) this.pre.style.opacity = fade;
      if (this.links) this.links.style.opacity = fade;
      if (this.fine) this.fine.style.opacity = fade;
    }
    this._statusBase = p < 0.15 ? 'SIGNAL LOCKED' : p < 0.78 ? 'SCRAMBLING…' : 'SCRAMBLED · TUNE TO UNLOCK';
    this._updateTone();
  }

  _tick = (now) => {
    if (!this._visible) { this._raf = null; return; }
    this._measure();
    const clarity = this._updateTuner(now) || 0;
    if (this.status) {
      const s = this.p < 0.15 ? (this._statusBase || 'SIGNAL LOCKED')
        : clarity > 0.9 ? 'SIGNAL LOCKED · HOLD IT'
        : clarity > 0.45 ? 'ALMOST… KEEP TURNING'
        : (this._statusBase || 'SCRAMBLED · TUNE TO UNLOCK');
      if (this.status.textContent !== s) this.status.textContent = s;
    }
    const gl = this.gl;
    const dpr = Math.min(1.5, window.devicePixelRatio || 1);
    const w = Math.round(this.canvas.clientWidth * dpr);
    const h = Math.round(this.canvas.clientHeight * dpr);
    if (w !== this._lastW || h !== this._lastH) {
      this.canvas.width = w; this.canvas.height = h;
      gl.viewport(0, 0, w, h);
      this._lastW = w; this._lastH = h;
    }

    // Pull the current background frame
    const v = this.video;
    let hasTex = 0;
    let ax = 1, ay = 1;
    if (v && v.readyState >= 2 && v.videoWidth > 0) {
      gl.bindTexture(gl.TEXTURE_2D, this.tex);
      try {
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, v);
        hasTex = 1;
        // cover-fit
        const ra = w / h, va = v.videoWidth / v.videoHeight;
        if (ra > va) ay = va / ra; else ax = ra / va;
      } catch { hasTex = 0; }
    }

    const acc = hex3(getComputedStyle(document.body).getPropertyValue('--accent'));
    gl.uniform2f(this.u.res, w, h);
    gl.uniform1f(this.u.time, (now - this._t0) / 1000);
    gl.uniform1f(this.u.p, this.p);
    gl.uniform1f(this.u.tune, clarity);
    gl.uniform1f(this.u.hasTex, hasTex);
    gl.uniform2f(this.u.aspect, ax, ay);
    gl.uniform3f(this.u.accent, acc[0], acc[1], acc[2]);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    this._raf = requestAnimationFrame(this._tick);
  };

  // ── scramble buzz: a low, quiet 60 Hz-ish hum while scrambled, audio on only ──
  _updateTone() {
    const want = this.audio?.isEnabled && this._visible && this.p > 0.3 && (this.tune?.clarity ?? 0) < 0.85;
    if (want && !this._tone) {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sawtooth'; osc.frequency.value = 58;
        gain.gain.value = 0.0001;
        osc.connect(gain).connect(ctx.destination);
        osc.start();
        gain.gain.exponentialRampToValueAtTime(0.012, ctx.currentTime + 0.6);
        this._tone = { ctx, osc, gain };
      } catch { /* no audio */ }
    } else if (!want && this._tone) {
      this._stopTone();
    }
  }

  _stopTone() {
    const t = this._tone; if (!t) return;
    this._tone = null;
    try {
      t.gain.gain.exponentialRampToValueAtTime(0.0001, t.ctx.currentTime + 0.4);
      setTimeout(() => { try { t.osc.stop(); t.ctx.close(); } catch { /* ignore */ } }, 500);
    } catch { /* ignore */ }
  }

  destroy() {
    window.removeEventListener('resize', this._onResize);
    if (this._retune) document.removeEventListener('randomize', this._retune);
    this._themeObs?.disconnect();
    this._io?.disconnect();
    if (this._raf) cancelAnimationFrame(this._raf);
    this._stopTone();
    if (this.background) this.background.style.opacity = '';
  }
}
